import { useAtom } from 'jotai'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import { omanDuplicateOrderAtom } from '../../../stores/oman.store'
import LemonadeIcon from '../assets/LemonadeIcon.svg?react'

// Ported from the legacy ushop-feelgreat-2 `DuplicatedOrder.js` Mobx observer.
// Same visual layout (LemonadeIcon → title → description with payment_id /
// date_and_time / total_price substitutions → Place Order button → Cancel link)
// but converted to:
//   - TypeScript + React function component
//   - Jotai (`omanDuplicateOrderAtom`) instead of Mobx stores
//   - MUI `Dialog` wrapper (no external front-drop state required)
//   - Native `Intl` formatters instead of `moment-timezone` / `NumberHelpers`
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatDateTime = (iso: string) => {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    const dd = String(d.getDate()).padStart(2, '0')
    const mon = MONTHS[d.getMonth()]
    const yyyy = d.getFullYear()
    const HH = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    const ss = String(d.getSeconds()).padStart(2, '0')
    return `${dd}-${mon}-${yyyy} at ${HH}:${mm}:${ss}`
}

const formatCurrency = (amount: number, currency: string) => {
    // Swap position: number first, currency code after — matches legacy NumberHelpers
    // output used in `duplicated_order_description` translation.
    const n = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
    return `${n} ${currency}`
}

const DuplicateOrderDialog = ({ onProceed, onCancel }: { onProceed: () => void; onCancel: () => void }) => {
    const { translate } = useTranslate()
    const [dupData, setDupData] = useAtom(omanDuplicateOrderAtom)

    const paymentId = dupData?.payments_id ?? dupData?.gateway_ref_id ?? dupData?.order_number ?? ''
    const dateAndTime = dupData?.date_time ? formatDateTime(dupData.date_time) : ''
    const totalPrice = dupData?.total_price != null ? formatCurrency(dupData.total_price, dupData.currency ?? 'OMR') : ''

    const description = translate('duplicated_order_description')
        .replace('{{payment_id}}', String(paymentId))
        .replace('{{date_and_time}}', dateAndTime)
        .replace('{{total_price}}', totalPrice)

    const handleCancel = () => {
        setDupData(null)
        onCancel()
    }

    const handleProceed = () => {
        setDupData(null)
        onProceed()
    }

    const isDuplicate = !!dupData

    return (
        <Dialog
            open={isDuplicate}
            onClose={handleCancel}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    px: { xs: 2, sm: 4 },
                    py: { xs: 3, sm: 5 },
                    maxWidth: 480,
                    width: '100%'
                }
            }}
            data-testid="duplicate-order-dialog-dialog">
            <Stack justifyContent="center" alignItems="center" width="100%" data-testid="duplicate-order-dialog-stack-lemonade-icon">
                <LemonadeIcon data-testid="duplicate-order-dialog-lemonade-icon" />

                <Stack mt="32px" textAlign="center" data-testid="duplicate-order-dialog-stack-typography-duplicated-order-title">
                    <Typography
                        variant="h3"
                        sx={{ fontSize: { xs: 22, sm: 24 }, fontWeight: 700, color: '#003764' }}
                        data-testid="duplicate-order-dialog-typography-duplicated-order-title">
                        {translate('duplicated_order_title')}
                    </Typography>
                </Stack>

                <Stack mt="16px" textAlign="center" data-testid="duplicate-order-dialog-stack-typography">
                    <Typography sx={{ fontSize: 16, color: '#003764' }} data-testid="duplicate-order-dialog-typography">
                        {description}
                    </Typography>
                </Stack>

                <Stack
                    mt="32px"
                    textAlign="center"
                    justifyContent="center"
                    alignItems="center"
                    width={{ xs: '100%', sm: '100%', md: '325px' }}
                    data-testid="duplicate-order-dialog-stack-button-handle-proceed">
                    <Button
                        onClick={handleProceed}
                        fullWidth
                        variant="contained"
                        sx={{
                            bgcolor: '#5A8FC4',
                            height: '48px',
                            fontSize: '20px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '99px',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#4A7FB4', boxShadow: 'none' }
                        }}
                        data-testid="duplicate-order-dialog-button-handle-proceed">
                        {translate('place_order')}
                    </Button>

                    <Typography
                        mt="16px"
                        onClick={handleCancel}
                        sx={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: '#003B6F',
                            textDecoration: 'underline',
                            textUnderlineOffset: '3px',
                            cursor: 'pointer'
                        }}
                        data-testid="duplicate-order-dialog-typography-handle-cancel">
                        {translate('cancel')}
                    </Typography>
                </Stack>
            </Stack>
        </Dialog>
    )
}

export default DuplicateOrderDialog
